import React from "react";
import { Outlet, useLocation, Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { FaArrowRight } from "react-icons/fa";
import { RootState } from "@/store";
import SidebarNav from "@/components/SidebarNav";
import AdminNav from "@/components/AdminNav";
import AnimatedBackground from "../AnimatedBackground";
import Footer from "../Footer";
import { AimLogo } from "../icons/AimLogo";
import AccountSettingsModal from "@/components/AccountSettingsModal";
import { useAccountSettingsModal } from "@/contexts/AccountSettingsModalContext";

const PublicHeader: React.FC = () => {
  return (
    <header className="absolute w-full px-6 top-0 z-50 h-fit">
      <div className="max-w-[1440px] mx-auto px-0 py-[30px] flex items-center justify-between">
        <div className="flex items-center gap-12 bg-white/10 backdrop-blur-[44px] py-3.5 px-5 rounded-[14px]">
          <Link to="/" className="no-underline">
            <AimLogo />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <NavLink
              to="/product"
              className={({ isActive }) =>
                `text-base font-medium no-underline transition-colors ${isActive ? "text-[#2563EB]" : "text-white hover:text-white/80"}`
              }
            >
              Product
            </NavLink>
            <NavLink
              to="/about"
              className={({ isActive }) =>
                `text-base font-medium no-underline transition-colors ${isActive ? "text-[#2563EB]" : "text-white hover:text-white/80"}`
              }
            >
              About
            </NavLink>
            <NavLink
              to="/privacy-policy"
              className={({ isActive }) =>
                `text-base font-medium no-underline transition-colors ${isActive ? "text-[#2563EB]" : "text-white hover:text-white/80"}`
              }
            >
              Privacy
            </NavLink>
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            className="text-base font-medium no-underline text-white hover:text-white/80 transition-colors"
          >
            Contact sales
          </Link>
          <Link
            to="/login"
            className="text-base font-medium no-underline text-white hover:text-white/80 transition-colors"
          >
            Sign In
          </Link>
          <Link
            to="/login"
            className="flex items-center backdrop-blur-[30px] gap-2 px-4 py-2 rounded-[14px] text-base font-medium no-underline text-white bg-white/10 hover:opacity-90 transition-opacity"
          >
            Create Your First Campaign <FaArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </header>
  );
};

const Layout: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, user } = useSelector(
    (state: RootState) => state.auth,
  );
  const { isOpen, closeModal } = useAccountSettingsModal();
  const isAdmin = user?.is_admin === true;
  const isOnAdminRoute = location.pathname.startsWith("/admin");
  const showAdminNav = isAdmin && isOnAdminRoute;
  const disablePageTransition = React.useMemo(() => {
    if (!location.pathname.startsWith("/my-campaigns")) return false;
    if (typeof window === "undefined") return false;

    return window.sessionStorage.getItem("openCreateCampaignModal") === "true";
  }, [location.pathname]);

  if (isAuthenticated) {
    return (
      <div className="relative min-h-screen flex flex-row overflow-x-hidden bg-[#f8f8f8]">
        <SidebarNav />
        <main className="w-[calc(100%_-_62px)] ml-[62px] flex-1 min-h-screen flex flex-col">
          {disablePageTransition ? (
            <div className="flex-1 p-[10px_10px_10px_0px]">
              <div className="relative main-layout main-container border border-[#E3E2E7] bg-white rounded-[15px] backdrop-blur-[10px] h-full">
                {showAdminNav && <AdminNav />}
                <Outlet />
              </div>
            </div>
          ) : (
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={
                  location.pathname.startsWith("/social-ads")
                    ? "/social-ads"
                    : location.pathname.startsWith("/landing-pages")
                      ? "/landing-pages"
                      : location.pathname.startsWith("/landing-page-builder")
                        ? "/landing-page-builder"
                        : location.pathname
                }
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.2 }}
                className="flex-1 p-[10px_10px_10px_0px]"
              >
                <div className="relative main-layout main-container border border-[#E3E2E7] bg-white rounded-[15px] backdrop-blur-[10px] h-full">
                  {showAdminNav && <AdminNav />}
                  <Outlet />
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </main>
        <AccountSettingsModal isOpen={isOpen} onClose={closeModal} />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex flex-col overflow-x-hidden public-layout">
      <AnimatedBackground />
      <PublicHeader />
      <main className="flex-1 relative flex flex-col">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
        <Footer />
      </main>
    </div>
  );
};

export default Layout;
